import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode
} from 'react';
import type { AuthenticationResult } from '@azure/msal-browser';
import {
  login as msalLogin,
  logout as msalLogout
} from './authService';
import { msalInstance } from './authService';

//
// 1) Define what your context holds
//
interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  account?: AuthenticationResult;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

//
// 2) Create the context (start as undefined so we can catch missing provider)
//
const AuthContext = createContext<AuthState | undefined>(undefined);

//
// 3) Props for your provider (so children is typed!)
//
interface AuthProviderProps {
  children: ReactNode;
}

//
// 4) The actual provider component
//
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [account, setAccount]       = useState<AuthenticationResult>();

  // Stable login function
  const login = useCallback(async () => {
    const result = await msalLogin();

    // MSAL-web returns AuthenticationResult (with .account),
    // mobile plugin returns { accessToken }
    if ('account' in result && result.account) {
      setAccessToken(result.accessToken);
      setAccount(result);

      // 👇 NEW LINE: this tells MSAL who the current user is
    msalInstance.setActiveAccount(result.account);
    } else {
      setAccessToken(result.accessToken);
      setAccount(undefined);
    }
  }, []);

  // Stable logout
  const logout = useCallback(async () => {
    await msalLogout();
    setAccessToken(null);
    setAccount(undefined);
  }, []);

  const value: AuthState = {
    isAuthenticated: !!accessToken,
    accessToken,
    account,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

//
// 5) Hook for consuming the context
//
export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
};
